import React from 'react'
import {Colors, FontSizeInPixel, FontWeights} from '../../../../styledComponents/Constants'
import styled, { ThemeProvider } from 'styled-components';
import {MESSAGE_CATEGORY} from '../../../../CodeMaster'
import {MessageFile as ResidentFile} from '../repository/NatGetMessage'
import {MainContainer} from '../../../../styledComponents/ContentsArea'
import {StyledDiv, StyledH3, StyledSpan} from '../../../../styledComponents/StyledTypeDefinitions'
import {InjectedProps} from '../../common/admin/NatCreatePreviewForResident'
import {MessageFileList as AdminRegisterFile} from '../repository/NatPostMessage'
import {openPdfPreviewWindow} from '../../../common/util/Natutil'
import {formatDateSlashSeparator} from '../../../../formatters/formatDate'
import {BaseResidentTheme, BaseResidentThemeForAdmin} from '../../../../styledComponents/common/FillGapResidentAndAdmin'
import {Box, StyledEngineProvider} from '@mui/material'
import withStyles from '@mui/styles/withStyles';
import {MessageFileList as AdminEditFile} from '../repository/NatPutMessage'
import {FullWidthLeftRowFlexContainer, LeftRowFlexContainer} from '../../../../styledComponents/FlexComponents'
import {faFilePdf} from '@fortawesome/free-solid-svg-icons'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {BlockSpacer} from '../../../../styledComponents/ResponsiveConvenience'
import {StyledLink} from '../../../../styledComponents/adminPage/Links'
import {CategoryLabel} from '../NatResidentMessageListPage'


// TODO: @mui/styles用 styled-componentsを使用するため削除してDefaultThemeはstyled-componentsの物を使用する
// declare module '@mui/styles/defaultTheme' {
//   // eslint-disable-next-line @typescript-eslint/no-empty-interface
//   interface DefaultTheme extends Theme {}
// }


interface Props {
    categoryId?: string
    publishStartDate?: string
    title?: string
    description?: string
    messageFile?: ResidentFile[] | AdminRegisterFile[] | AdminEditFile[]
}

export const NatMessageForResidentComponent: React.FC<Props & InjectedProps> = props => {
    const {categoryId, publishStartDate, title, description, messageFile, isResident} = props

    if (!categoryId || !publishStartDate || !title || !description) {
        return null
    }

    const category = MESSAGE_CATEGORY.codeToEnumConstants(categoryId)
    const files: (ResidentFile | AdminRegisterFile | AdminEditFile)[] | undefined = (messageFile) ?
        JSON.parse(JSON.stringify(messageFile)) : messageFile

    return <>
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={(isResident) ? BaseResidentTheme : BaseResidentThemeForAdmin}>
                <MainContainer>
                    <LeftRowFlexContainer>
                        <PublishStartDateItem>
                            {formatDateSlashSeparator(publishStartDate)}
                        </PublishStartDateItem>
                        <Box ml={'8px'}/>
                        <CategoryItem>
                            <CategoryLabel categoryId={categoryId}>
                                {category.label}
                            </CategoryLabel>
                        </CategoryItem>
                    </LeftRowFlexContainer>
                    <MessageTitle>{title}</MessageTitle>
                    <MessageDescription dangerouslySetInnerHTML={{__html: description}}/>
                    <Box height={'10px'}/>
                    {files && files.sort((a, b) => a.sortOrder - b.sortOrder).map((file, index) => {
                        return (
                            <AttachmentFile key={`messageFile_${index}`}>
                                {FileOpenedComponent(file)}
                            </AttachmentFile>
                        )
                    })}
                    <BlockSpacer height={'15px'}/>
                </MainContainer>
            </ThemeProvider>
        </StyledEngineProvider>
    </>;
}

const FileOpenedComponent = (messageFile: ResidentFile | AdminRegisterFile | AdminEditFile) => {

    if (!messageFile) {
        return <></>
    }
    if ((implementsResidentFile(messageFile))) {
        // 居住者画面
        return (
            <AttachmentFileLink to={messageFile.path} target={'_blank'}>
                <FullWidthLeftRowFlexContainer>
                    <FontAwesomeIcon icon={faFilePdf} color={'#FF6378'} size={'lg'}/>
                    <AttachmentFileName>{messageFile.publishMaterialName}</AttachmentFileName>
                </FullWidthLeftRowFlexContainer>
            </AttachmentFileLink>
        )
    } else if (implementsAdminRegisterFile(messageFile)) {
        // 管理画面（登録）
        return (
            <AttachmentFileLink onClick={() => previewPdf(messageFile.fileData as string)} target={'_blank'}>
                <FullWidthLeftRowFlexContainer>
                    <FontAwesomeIcon icon={faFilePdf} color={'#FF6378'} size={'lg'}/>
                    <AttachmentFileName>{messageFile.publishMaterialName}</AttachmentFileName>
                </FullWidthLeftRowFlexContainer>
            </AttachmentFileLink>
        )
    } else if (implementsAdminEditFile(messageFile)) {
        // 管理画面（編集）
        return (
            <AttachmentFileLink onClick={() => previewPdf(messageFile.fileData as string)} target={'_blank'}>
                <FullWidthLeftRowFlexContainer>
                    <FontAwesomeIcon icon={faFilePdf} color={'#FF6378'} size={'lg'}/>
                    <AttachmentFileName>{messageFile.publishMaterialName}</AttachmentFileName>
                </FullWidthLeftRowFlexContainer>
            </AttachmentFileLink>
        )
    }
    return <></>
}

const CategoryItem: StyledDiv = styled.div`
    padding-right: 45px;
    display: flex;
`

const PublishStartDateItem: StyledDiv = styled.div`
    color: #979797;
    font-size: ${FontSizeInPixel.Small}px;
`

const MessageTitle: StyledH3 = styled.h3`
    font-size: ${(props) => props.theme.fontSize.Accent};
    font-weight: ${FontWeights.Normal};
    margin: 20px 0 20px 0;
    word-break: break-all;
`

const MessageDescription: StyledSpan = styled.span`
    word-break: break-all;
`

function implementsResidentFile(arg: any): arg is ResidentFile {
    return arg !== null &&
        typeof arg === 'object' &&
        typeof arg.fileNumber === 'number' &&
        typeof arg.sortOrder === 'number' &&
        typeof arg.publishMaterialName === 'string' &&
        typeof arg.fileName === 'string' &&
        typeof arg.path === 'string'
}

function implementsAdminRegisterFile(arg: any): arg is AdminRegisterFile {
    return arg !== null &&
        typeof arg === 'object' &&
        typeof arg.sortOrder === 'number' &&
        typeof arg.publishMaterialName === 'string' &&
        typeof arg.fileName === 'string' &&
        (typeof arg.fileData === 'string' || 'ArrayBuffer' || 'null') &&
        typeof arg.fileSize === 'number'
}

function implementsAdminEditFile(arg: any): arg is AdminEditFile {
    return arg !== null &&
        typeof arg === 'object' &&
        (!arg.fileNumber || typeof arg.fileNumber === 'number') &&
        typeof arg.sortOrder === 'number' &&
        typeof arg.publishMaterialName === 'string' &&
        typeof arg.fileName === 'string' &&
        (!arg.fileData || typeof arg.fileData === ('string' || 'ArrayBuffer' || 'null')) &&
        (!arg.fileSize || typeof arg.fileSize === 'number') &&
        (!arg.path || typeof arg.path === 'string')
}

function previewPdf(dataUri: string): void {
    if (dataUri) {
        const data: string[] = dataUri.split(',')
        if (data[1]) {
            openPdfPreviewWindow(data[1])
        }
    }
}

NatMessageForResidentComponent.defaultProps = {
    isResident: true,
}

NatMessageForResidentComponent.displayName = 'NatMessageForResidentComponent'
export default NatMessageForResidentComponent

const AttachmentFileName = styled.span`
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    padding-left: 10px;
    padding-right: 10px;
    width: 100%;
`
const AttachmentFile = styled.div`
    border: 1px solid #D9D9D9;
    border-radius: 4px;
    margin-top: 10px;
    padding: 20px 10px;
`

const AttachmentFileLink = withStyles({
    root: {
        color: Colors.Body,
        fontSize: FontSizeInPixel.Body,
        lineHeight: 1.5,
    },
})(StyledLink)
